import React, { useEffect, useMemo, useState } from "react"

import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import {
    COMMON_ONE_VERIFY_POLICIES_CONFIG,
    PoliciesListType,
    PolicyAssignmentPriority,
    PolicyReassignmentReason,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config.ts"
import {
    IOneVerifyBillerGroup,
    IOneVerifyCoverageCheckHistory,
    IOneVerifyOrganizationUser,
} from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { AutoCompleteElement } from "@front-shared/elements/autoComplete.element.tsx"
import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { InputElement } from "@front-shared/elements/input.element.tsx"
import { PopupWrapperElement } from "@front-shared/elements/popupWrapper.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { SelectElement } from "@front-shared/elements/select.element.tsx"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper.tsx"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper.tsx"
import { useStateCallback } from "@front-shared/hooks/useStateCallback.hook.ts"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types.ts"
import type { IPopupProps } from "@front-shared/types/props.types.ts"

import { LookupHelper } from "../..//helpers/lookup.helper.tsx"
import { POLICIES_CONFIG } from "../../config/policies.config"
import { CoverageHistoryHelper } from "../../helpers/coverageHistory.helper"
import { RuntimeHelper } from "../../helpers/runtime.helper"
import { UtilHelper } from "../../helpers/util.helper"
import { useHasPermissions } from "../../hooks/hasPermissions.hook"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook.ts"
import { usePoliciesList } from "../../hooks/policiesList.hook.ts"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { billerGroupsSelector } from "../../store/selectors/billerGroups.selector"
import { organizationSelector } from "../../store/selectors/organization.selector"
import { profileSelector } from "../../store/selectors/profile.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { billerGroupsGetList } from "../../store/thunks/billerGroups.thunks"
import { organizationUsersGetList } from "../../store/thunks/organization.thunks"
import {
    taskAssignmentsBulkAssign,
    taskAssignmentsBulkReassign,
} from "../../store/thunks/taskAssignments.thunks.ts"
import { IAssignmentIdentification } from "../../types/objects.types.ts"

import closeIcon from "@front-shared/assets/images/icons/close-dark.svg"
import searchIcon from "@front-shared/assets/images/icons/search-dark.svg"
import slidersIcon from "@front-shared/assets/images/icons/sliders.svg"

export const TaskAssignmentAssigneePopupComponent: React.FunctionComponent<
    ITaskAssignmentAssigneePopupComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const organizationState = useAppSelector(organizationSelector)

    const billerGroups = useAppSelector(billerGroupsSelector)

    const profile = useAppSelector(profileSelector)

    const runtimeState = useAppSelector(runtimeSelector)

    const { onSuccessReloadList: onSuccessReloadListShared } = usePoliciesList()

    const [selectedUser, setSelectedUser] =
        useStateCallback<IOneVerifyOrganizationUser | null>(null)

    const [selectedBillerGroup, setSelectedBillerGroup] =
        useStateCallback<IOneVerifyBillerGroup | null>(null)

    const [selectedReassignmentReason, setSelectedReassignmentReason] =
        useStateCallback<PolicyReassignmentReason | null>(null)

    const [selectedPriority, setSelectedPriority] =
        useState<PolicyAssignmentPriority>(
            COMMON_ONE_VERIFY_POLICIES_CONFIG.defaultAssignmentPriority,
        )

    const [searchTerm, setSearchTerm] = useState<string>("")

    const [isShowingConfirmation, setIsShowingConfirmation] =
        useState<boolean>(false)

    const canAccessBillerGroups = useHasPermissions(
        OneVerifyPermission.manageTaskAssignments,
        undefined,
        false,
    )

    const hasReassignmentReasonFeatureFlag = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_TASKS_REASSIGNMENT_REASON,
    )

    // Auto-select current user when in biller-only mode
    useEffect(() => {
        if (!props.isBillerOnlyMode) {
            return
        }

        if (profile?.id) {
            const currentUser = organizationState.users.find(
                user => user.id === profile?.id,
            )

            if (currentUser) {
                setSelectedUser(currentUser)
            }
        }
    }, [
        props.isBillerOnlyMode,
        profile?.id,
        organizationState.users,
        selectedUser,
    ])

    // Determine whether to show the reassignment reason select
    const showReassignmentReasonSelect = useMemo<boolean>(() => {
        // Don't show the reassignment reason select if:
        // - No coverage checks to assign
        // - Biller only mode
        // - Reassignment reason feature flag is not enabled
        if (
            !props.coverageChecksToAssign?.length ||
            props.isBillerOnlyMode ||
            !hasReassignmentReasonFeatureFlag
        ) {
            return false
        }

        // Show the reassignment reason select if any coverage checks have existing assignments (reassigns)
        return props.coverageChecksToAssign.some(coverageCheck => {
            const assignment = coverageCheck.policy?.latestAssignment

            return (
                assignment &&
                POLICIES_CONFIG.activeAssignmentStatuses.includes(
                    assignment.status,
                )
            )
        })
    }, [
        props.coverageChecksToAssign,
        props.isBillerOnlyMode,
        hasReassignmentReasonFeatureFlag,
    ])

    // Determine whether to show the priority select
    // Only show if ALL coverage checks are new assignments (no existing active assignments)
    const showPrioritySelect = useMemo<boolean>(() => {
        if (!props.coverageChecksToAssign?.length) {
            return false
        }

        // Check if any coverage checks have existing active assignments
        const hasAnyExistingAssignments = props.coverageChecksToAssign.some(
            coverageCheck => {
                const assignment = coverageCheck.policy?.latestAssignment

                return (
                    assignment &&
                    POLICIES_CONFIG.activeAssignmentStatuses.includes(
                        assignment.status,
                    )
                )
            },
        )

        // Show priority select only if there are no existing assignments
        return !hasAnyExistingAssignments
    }, [props.coverageChecksToAssign])

    const onBillerGroupSelected = (group: IOneVerifyBillerGroup) => {
        setSelectedBillerGroup(group)

        const selectedUserInGroup = group?.users?.find(
            user => user.id === selectedUser?.id,
        )

        // Clear user selection if user is not in group
        if (!selectedUserInGroup) {
            setSelectedUser(null)
        }
    }

    const isAssigning = useMemo<boolean>(() => {
        return (
            RuntimeHelper.isTaskAssignmentsBulkAssignLoading() ||
            RuntimeHelper.isTaskAssignmentsBulkReassignLoading()
        )
    }, [runtimeState.isLoading])

    // Filter users based on search term and selected biller group
    const filteredUsers = useMemo(() => {
        const usersToFilter =
            selectedBillerGroup?.users || organizationState.users

        // Apply search term filter to the current user list (full list or biller group filtered list)
        if (!searchTerm) {
            return usersToFilter
        }

        return usersToFilter.filter(user =>
            LookupHelper.getRenderedOrganizationUser(user)
                .displayValue.toLowerCase()
                .includes(searchTerm.toLowerCase()),
        )
    }, [organizationState.users, searchTerm, selectedBillerGroup])

    const availableBillerGroupsOptions = useMemo<
        ISelectRenderedOption[]
    >(() => {
        return billerGroups.items.map(group => {
            const rendered = LookupHelper.getRenderedBillerGroup(group)
            return {
                displayValue: rendered.displayValue,
                value: rendered.value,
            }
        })
    }, [billerGroups.items])

    const priorityMapping = useMemo(() => {
        return POLICIES_CONFIG.policyAssignmentPriorityMapping(
            props.policiesListType,
        )
    }, [props.policiesListType])

    // Preload organization users on mount
    useEffect(() => {
        if (!selectedPracticeRole || organizationState.users.length) {
            return
        }

        dispatch(
            organizationUsersGetList({
                payload: undefined,
                practice: selectedPracticeRole.practice,
            }),
        )
    }, [selectedPracticeRole?.id])

    // Preload biller groups on mount
    useEffect(() => {
        if (
            !selectedPracticeRole ||
            !canAccessBillerGroups ||
            billerGroups.items.length
        ) {
            return
        }

        dispatch(
            billerGroupsGetList({
                payload: UtilHelper.getPagination(100),
                practice: selectedPracticeRole.practice,
            }),
        )
    }, [selectedPracticeRole?.id, canAccessBillerGroups])

    const onSuccessReloadList = (
        results: (IOneVerifyCoverageCheckHistory | undefined)[],
    ) => {
        onSuccessReloadListShared(props.policiesListType, results)

        onDone()
    }

    const assignPolicies = (nirvanaRequestIds: string[]) => {
        if (
            !selectedPracticeRole ||
            !nirvanaRequestIds.length ||
            !selectedUser
        ) {
            return
        }

        const totalCount = nirvanaRequestIds.length

        dispatch(
            taskAssignmentsBulkAssign({
                practice: selectedPracticeRole.practice,
                payload: {
                    nirvanaRequestIds,
                    assigneeId: selectedUser.id,
                    priority: selectedPriority,
                },
                onSuccess: results => {
                    const successCount = results.length
                    const failureCount = totalCount - successCount

                    // Show toast notification with results
                    if (failureCount <= 0) {
                        // All succeeded
                        ToastrHelper.success(
                            `Successfully assigned ${successCount} ${
                                successCount === 1 ? "policy" : "policies"
                            }`,
                        )
                    } else if (successCount > 0) {
                        // Partial success
                        ToastrHelper.warning(
                            `Assigned ${successCount} of ${totalCount} ${
                                totalCount === 1 ? "policy" : "policies"
                            }. ${failureCount} ${failureCount === 1 ? "assignment" : "assignments"} failed.`,
                        )
                    } else {
                        // All failed
                        ToastrHelper.error(
                            `Failed to assign ${totalCount} ${
                                totalCount === 1 ? "policy" : "policies"
                            }.`,
                        )
                    }

                    onSuccessReloadList(results)
                },
            }),
        )
    }

    const reassignPolicies = (assignments: IAssignmentIdentification[]) => {
        if (
            !selectedPracticeRole ||
            !assignments.length ||
            !selectedUser ||
            (!!hasReassignmentReasonFeatureFlag && !selectedReassignmentReason)
        ) {
            return
        }

        dispatch(
            taskAssignmentsBulkReassign({
                practice: selectedPracticeRole.practice,
                payload: {
                    assignments,
                    assigneeId: selectedUser.id,
                    reassignmentReason: selectedReassignmentReason || undefined,
                },
                onSuccess: onSuccessReloadList,
            }),
        )
    }

    const onAssign = () => {
        if (
            !selectedPracticeRole ||
            !props.coverageChecksToAssign?.length ||
            !selectedUser
        ) {
            return
        }

        // Filter out items that are not eligible for assignment
        const validChecks = (props.coverageChecksToAssign || []).filter(
            coverageCheck =>
                !!CoverageHistoryHelper.getPolicyIdentificationData(
                    coverageCheck,
                ),
        )

        if (validChecks.length === 0) {
            ToastrHelper.warning("No valid policies to assign.")

            onDone()

            return
        }

        // Create reassignPoliciesList first - policies with existing active assignments
        const reassignPoliciesList = validChecks.filter(
            coverageCheck =>
                coverageCheck.policy?.latestAssignment &&
                POLICIES_CONFIG.activeAssignmentStatuses.includes(
                    coverageCheck.policy.latestAssignment.status,
                ),
        )

        // Filter reassignPoliciesList further by assignee id to get policies needing reassignment
        // (policies that are already assigned but to a different assignee, excluding done statuses)
        const policiesNeedingReassignment = reassignPoliciesList.filter(
            coverageCheck => {
                const assignment = coverageCheck.policy?.latestAssignment

                // Skip policies with done statuses - they should be treated as already assigned
                if (
                    !assignment ||
                    COMMON_ONE_VERIFY_POLICIES_CONFIG.doneAssignmentStatuses.includes(
                        assignment.status,
                    )
                ) {
                    return false
                }

                // Check if assignee is different from selected user
                return assignment.assignee?.id !== selectedUser.id
            },
        )

        // Create assignPoliciesList - policies that need initial assignment
        // (policies without assignments or with inactive assignments)
        const assignPoliciesList = validChecks.filter(
            coverageCheck =>
                !coverageCheck.policy?.latestAssignment ||
                !POLICIES_CONFIG.activeAssignmentStatuses.includes(
                    coverageCheck.policy.latestAssignment.status,
                ),
        )

        // Check if all selected policies already have the same assignee or are in done status
        const allPoliciesHaveSameAssignee =
            reassignPoliciesList.length > 0 &&
            reassignPoliciesList.every(coverageCheck => {
                const assignment = coverageCheck.policy?.latestAssignment
                if (!assignment) {
                    return false
                }

                // If assignment is in done status, treat as if already assigned to selected user
                if (
                    COMMON_ONE_VERIFY_POLICIES_CONFIG.doneAssignmentStatuses.includes(
                        assignment.status,
                    )
                ) {
                    return true
                }

                // Otherwise check if assignee matches selected user
                return assignment.assignee?.id === selectedUser.id
            })

        // Check if there are any policies that need action (assignment or reassignment)
        const hasPoliciesNeedingAction =
            assignPoliciesList.length > 0 ||
            policiesNeedingReassignment.length > 0

        // If all policies already have the same assignee AND no new assignments needed, show info alert and return
        if (
            (allPoliciesHaveSameAssignee && assignPoliciesList.length === 0) ||
            !hasPoliciesNeedingAction
        ) {
            ToastrHelper.warning(
                "No action taken. All selected policies are already assigned to the selected user.",
            )

            onDone()

            return
        }

        // Additional confirmation scenarios
        const totalAssignments = validChecks.length

        const hasMultipleAssignments = totalAssignments > 1

        const onTriggerAssignments = () => {
            // Call assignPolicies for policies without existing assignments
            if (assignPoliciesList.length) {
                const nirvanaRequestIds = assignPoliciesList
                    .map(
                        coverageCheck => coverageCheck.coverageResult?.resultId,
                    )
                    .filter(Boolean)
                    .map(resultId => resultId.toString())

                assignPolicies(nirvanaRequestIds)
            }

            // Call reassignPolicies for policies with existing assignments that need reassignment
            if (policiesNeedingReassignment.length) {
                const assignments: IAssignmentIdentification[] =
                    policiesNeedingReassignment
                        .map(coverageCheck => {
                            return {
                                id: coverageCheck.policy?.latestAssignment?.id,
                                nirvanaRequestId:
                                    coverageCheck.coverageResult.resultId,
                            }
                        })
                        .filter(
                            item => !!item.id,
                        ) as IAssignmentIdentification[]

                reassignPolicies(assignments)
            }
        }

        // Calculate total changes that will be made
        const totalChanges =
            assignPoliciesList.length + policiesNeedingReassignment.length

        // If all selected policies will be changed, no confirmation needed
        if (totalChanges === totalAssignments) {
            onTriggerAssignments()
        } else if (hasMultipleAssignments && totalChanges > 0) {
            // Multiple policies selected but only some will be changed - show confirmation
            const selectedPersonName =
                LookupHelper.getRenderedOrganizationUser(
                    selectedUser,
                ).displayValue

            const message = `You will change ${totalChanges} assignment${totalChanges > 1 ? "s" : ""} to '${selectedPersonName}'. Not all selected policies will update. Updates apply only if a policy has an Assignee, the new Assignment differs from the current one, and the current Status is active. Confirm to apply this change.`

            // Hide the popup when showing confirmation
            setIsShowingConfirmation(true)

            ConfirmationHelper.enableConfirmation(
                () => {
                    setIsShowingConfirmation(false)
                    onTriggerAssignments()
                },
                "Confirm Assignment Change",
                message,
                () => {
                    // Show popup again when canceled and clear selections
                    setIsShowingConfirmation(false)
                },
            )
        } else {
            onTriggerAssignments()
        }
    }

    const onDone = () => {
        setSelectedUser(null)
        setSelectedBillerGroup(null)
        setIsShowingConfirmation(false)
        setSearchTerm("")
        setSelectedPriority(
            COMMON_ONE_VERIFY_POLICIES_CONFIG.defaultAssignmentPriority,
        )

        props.onDone()
    }

    return (
        <>
            <PopupWrapperElement
                isActive={props.isActive}
                className={`
                    ${props.className}
                    ${isShowingConfirmation ? "hidden" : ""}
                `}
                onDone={onDone}
                closeOnOutsideClick={props.closeOnOutsideClick}
                popupWidthClassName={`${props.isBillerOnlyMode ? "p-[24px]!" : "h-[491px] p-[12px]!"} md:w-screen md:h-screen`}
            >
                <div className="flex flex-col h-full gap-[8px]">
                    <div className="flex items-center mb-[8px]">
                        <PrimaryText typography="h4" className="flex-1">
                            Assign {props.coverageChecksToAssign?.length || 0}{" "}
                            {props.coverageChecksToAssign?.length &&
                            props.coverageChecksToAssign.length > 1
                                ? `policies `
                                : `policy `}{" "}
                            {!props.isBillerOnlyMode ? "to..." : ""}
                        </PrimaryText>

                        <div className="cursor-pointer" onClick={onDone}>
                            <img src={closeIcon} alt="Close" title="Close" />
                        </div>
                    </div>

                    {/* Biller group / users selection hidden for billers */}
                    {!props.isBillerOnlyMode ? (
                        <>
                            <div className="relative mb-[8px]">
                                <InputElement
                                    inputSuffix={
                                        <img
                                            src={searchIcon}
                                            alt="Search"
                                            title="Filter"
                                        />
                                    }
                                    name="searchTerm"
                                    type="text"
                                    placeholder="Search for a verification specialist"
                                    defaultValue={searchTerm}
                                    onChange={e => {
                                        setSearchTerm(e.target.value)
                                    }}
                                />
                            </div>

                            {/* Billing groups dropdown - hidden for billers */}
                            {canAccessBillerGroups && (
                                <div className="mb-[8px]">
                                    <AutoCompleteElement
                                        name="billerGroupSelect"
                                        placeholder="Select a group"
                                        renderedOptions={
                                            availableBillerGroupsOptions
                                        }
                                        onSelected={onBillerGroupSelected}
                                        className="deep-purple-border"
                                        itemRenderer={item => item}
                                        inputPrefix={
                                            <img
                                                src={slidersIcon}
                                                alt="Assign From Group"
                                                title="Assign From Group"
                                            />
                                        }
                                    />
                                </div>
                            )}

                            {/* Users list - hidden for billers */}
                            <div className="flex-1 mb-[8px] min-h-[0]">
                                <div className="h-full overflow-y-auto bg-brand-white border-[1px] border-solid border-brand-grey7 rounded-[8px]">
                                    {filteredUsers.map((user, index) => {
                                        const isSelected =
                                            selectedUser?.id === user.id

                                        return (
                                            <div
                                                key={user.id}
                                                className={`flex items-center justify-between py-[6px] px-[16px] cursor-pointer border-b border-gray-200 transition-colors duration-200 ${
                                                    isSelected
                                                        ? "bg-brand-deepPurple"
                                                        : "bg-transparent hover:bg-brand-grey5"
                                                } ${
                                                    index ===
                                                    filteredUsers.length - 1
                                                        ? "border-b-0"
                                                        : ""
                                                }`}
                                                onClick={() => {
                                                    setSelectedUser(user)
                                                }}
                                            >
                                                <div className="flex-1">
                                                    <PrimaryText
                                                        typography="text"
                                                        color={
                                                            isSelected
                                                                ? "white"
                                                                : undefined
                                                        }
                                                    >
                                                        {
                                                            LookupHelper.getRenderedOrganizationUser(
                                                                user,
                                                            ).displayValue
                                                        }
                                                    </PrimaryText>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 mt-[8px] mb-[16px] min-h-[0]">
                            <PrimaryText typography="text">
                                {selectedUser
                                    ? `You will assign ${
                                          props.coverageChecksToAssign
                                              ?.length &&
                                          props.coverageChecksToAssign.length >
                                              1
                                              ? `these policies `
                                              : `this policy `
                                      } to yourself`
                                    : "Loading..."}
                            </PrimaryText>
                        </div>
                    )}

                    {/* Priority select - only show for new assignments */}
                    {showPrioritySelect && (
                        <div className="mb-[8px] mt-[24px]">
                            <SelectElement
                                name="taskPrioritySelect"
                                placeholder="Select a priority"
                                label="Priority"
                                renderedOptions={POLICIES_CONFIG.getRenderedPriorityOptions(
                                    props.policiesListType,
                                )}
                                externalValue={
                                    priorityMapping[selectedPriority]
                                }
                                onSelected={setSelectedPriority}
                                selectClassName="deep-purple-border"
                                size="large"
                            />
                        </div>
                    )}

                    {/* Reason for reassignment */}
                    {showReassignmentReasonSelect && (
                        <div className="mb-[8px] mt-[24px]">
                            <SelectElement
                                name="reassignmentReasonSelect"
                                placeholder="Select a reason"
                                label="Reason for reassignment"
                                renderedOptions={
                                    POLICIES_CONFIG.policyReassignmentReasonsRendered
                                }
                                onSelected={(
                                    reason: PolicyReassignmentReason,
                                ) => {
                                    setSelectedReassignmentReason(reason)
                                }}
                                selectClassName="deep-purple-border"
                                size="large"
                            />
                        </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex items-center justify-center gap-[8px]">
                        <ButtonElement
                            buttonClassName="w-[140px]"
                            label="Cancel"
                            type="default"
                            size="large"
                            htmlType="button"
                            onClick={onDone}
                        />

                        <ButtonElement
                            buttonClassName="w-[140px]"
                            label={isAssigning ? "Assigning..." : "Assign"}
                            type="primary"
                            size="large"
                            htmlType="button"
                            onClick={onAssign}
                            disabled={
                                !selectedUser ||
                                isAssigning ||
                                (showReassignmentReasonSelect &&
                                    !selectedReassignmentReason)
                            }
                            isLoading={isAssigning}
                        />
                    </div>
                </div>
            </PopupWrapperElement>
        </>
    )
}

interface ITaskAssignmentAssigneePopupComponentProps extends IPopupProps {
    coverageChecksToAssign?: IOneVerifyCoverageCheckHistory[]
    policiesListType: PoliciesListType
    isBillerOnlyMode?: boolean
}
